import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Table, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUndo } from "react-icons/fa";
import APIServicenew from "../../utils/APIGeneralService";
import { UserContext } from "../../context/UserContextProvider";
import { ToastContainer, toast } from "react-toastify";
import DeleteModal from "../../components/common/DeleteModel";
import GradeFilter from "./GradeFilter";
import useMenuPermissions from "../../components/CustomHook/custompermission";
import { formateDatemdy } from "../../utils/Helper";
import Paginator from "../../components/common/Pagination";

const ManageGrade = () => {
  const permissionscontext = useMenuPermissions("/admin/grade");
  const [gradeList, setGradeList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [count, setCount] = useState(0);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showDt, setShowDt] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const limit = 10;

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);

  const headers = {
    "Content-Type": "application/json",
    "auth-token": authToken,
    "authorization": accessToken
  };

  useEffect(() => {
    fetchGrades();
  }, [currentPage, filters, searchTerm]);

  const fetchGrades = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (currentPage - 1) * limit,
        limit: limit,
      };

      if (searchTerm && searchTerm.trim() !== "") {
        params.search = searchTerm.trim();
      } else if (filters.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }
     
      if (filters.status && filters.status !== "") {
        params.status = filters.status;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await APIServices.get(
        `/admin/cms/grade_list?${queryString}`,
        headers
      );

      if (response?.data?.status === true) {
        const gradesData = response?.data?.data?.grades || response?.data?.data?.data || [];
        setGradeList(gradesData);
        const total = response?.data?.data?.total || 0;
        setCount(total);
        setTotalCount(Math.ceil(total / limit) || 0);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setGradeList([]);
        setCount(0);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching grades:", error);
      showToast("Error fetching grades", "error");
      setGradeList([]);
      setCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (grade) => {
    const newStatus = grade.status === "Active" ? "Inactive" : "Active";
    try {
      const payload = { id: grade.id, status: newStatus };
      const res = await APIServices.put(
        `/admin/cms/grade_status`,
        payload,
        headers
      );

      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Status updated successfully", "success");
        // Refresh the list after status change
        fetchGrades();
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", "error");
    }
  };

  const handleDeleteGrade = async () => {
    try {
      if (selectedGrade !== null) {
        const payload = { id: selectedGrade };
        const res = await APIServices.delete(
          `/admin/cms/grade_delete`,
          payload,
          headers
        );

        if (res?.data?.status === true) {
          showToast(res?.data?.message || "Grade deleted successfully", "success");
          
          // Check if this was the last item on the page
          if (gradeList.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          } else {
            // Refresh the list
            fetchGrades();
          }
          
          setSelectedGrade(null);
        } else {
          showToast(res?.data?.message || "Something went wrong.", "error");
        }
      }
      setShowDt(false);
    } catch (error) {
      console.error("Error deleting grade:", error);
      showToast("Error deleting grade", "error");
      setShowDt(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    // The useEffect will trigger fetchGrades with the new searchTerm
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleApplyFilter = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setFilters({});
    setCurrentPage(1);
    setShowFilter(false);
  };

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  const getSerialNumber = (index) => {
    return (currentPage - 1) * limit + index + 1;
  };

  const truncateText = (text, limit = 100) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const renderWithTooltip = (text, limit = 100, placement = "top") => {
    if (!text) return "";

    if (text.length > limit) {
      return (
        <OverlayTrigger
          placement={placement}
          overlay={
            <Tooltip id={`tooltip-${Math.random()}`}>
              <div style={{ maxWidth: '400px', wordWrap: 'break-word' }}>
                {text}
              </div>
            </Tooltip>
          }
        >
          <span className="cursor-pointer" style={{ cursor: 'help' }}>
            {truncateText(text, limit)}
          </span>
        </OverlayTrigger>
      );
    }
    return text;
  };

  // Display active filters
  const hasActiveFilters = Object.keys(filters).some(key => filters[key] && filters[key] !== "");
  
  // Get filter display text
  const getFilterDisplayText = () => {
    const activeFilters = [];
    if (filters.search) activeFilters.push(`search: ${filters.search}`);
    if (filters.status) activeFilters.push(`Status: ${filters.status}`);
    return activeFilters.join(", ");
  };

  return (
    <>
      <div className="momList_inner todo_list_inner grade_list_inner">
        <div className="mom_header d-flex align-items-center justify-content-between mb-3">
          <h1 className="font_blue_18 font-600">
            {`Grade Management (${count})`}
          </h1>

          <ul className="d-flex align-items-center mom_filter">
            <li
              className="d-flex align-items-center cursor-pointer"
              onClick={() => setShowFilter(true)}
            >
              Filter By{" "}
              <img
                src="/images/Sort.svg"
                className="img-fluid ms-2"
                alt="Sort"
              />
            </li>
            <li>
              {permissionscontext.add == 1 && (
                <button
                  className="btn red_button d-flex align-items-center"
                  onClick={() => navigate("/admin/add-grade")}
                >
                  <FaPlus className="me-1" />
                  Add Grade
                </button>
              )}
            </li>
          </ul>
        </div>

        <div className="todoList_table">
          <div className="table-responsive">
            <Table>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '5%' }}>Sr.No</th>
                  <th style={{ width: '35%' }}>Grade Name</th>
                  <th style={{ width: '15%' }}>Status</th>
                  <th style={{ width: '15%' }}>Created Date</th>
                  <th style={{ width: '10%' }}>Manage</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : gradeList.length > 0 ? (
                  gradeList.map((grade, index) => (
                    <tr key={grade.id}>
                      <td>{getSerialNumber(index)}</td>
                      <td>
                        <div className="grade-name font-weight-bold">
                          {renderWithTooltip(grade.name, 100, "top")}
                        </div>
                      </td>
                      <td>
                        <Form className="Switch_toggle">
                          <input
                            type="checkbox"
                            id={`switch-${grade.id}`}
                            checked={grade.status === "Active"}
                            onChange={() => handleStatusChange(grade)}
                            disabled={!permissionscontext.edit}
                          />
                          <label
                            className="position-relative"
                            htmlFor={`switch-${grade.id}`}
                          >
                            Toggle
                          </label>
                        </Form>
                      </td>
                      <td>{formateDatemdy(grade.created_at)}</td>
                      <td>
                        <ul className="d-flex align-items-center list-unstyled mb-0">
                          <li className="me-2">
                            <button
                              className="btn btn-sm btn-outline-success py-1"
                              onClick={() => {
                                if (permissionscontext.edit) {
                                  navigate(`/admin/update-grade/${grade.id}`, {
                                    state: { row: grade },
                                  });
                                }
                              }}
                              disabled={!permissionscontext.edit}
                              style={{
                                opacity: permissionscontext.edit ? 1 : 0.5,
                                cursor: permissionscontext.edit ? "pointer" : "not-allowed",
                              }}
                            >
                              <FaEdit />
                            </button>
                          </li>
                          <li>
                            <button
                              className="btn btn-sm btn-outline-danger py-1"
                              onClick={() => {
                                if (permissionscontext.delete) {
                                  setSelectedGrade(grade.id);
                                  setShowDt(true);
                                }
                              }}
                              disabled={!permissionscontext.delete}
                              style={{
                                opacity: permissionscontext.delete ? 1 : 0.5,
                                cursor: permissionscontext.delete ? "pointer" : "not-allowed",
                              }}
                            >
                              <FaTrash />
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <div className="text-muted">
                        <h5>No Grades found</h5>
                        <p>
                          {hasActiveFilters || searchTerm 
                            ? "Try clearing filters or search terms" 
                            : "Click on 'Add Grade' to create your first grade"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="pagination_box mt-4">
          {count > limit && (
            <Paginator
              currentPage={currentPage}
              totalPages={totalCount}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilter && (
        <GradeFilter
          appliedFilters={filters}
          show={showFilter}
          onHide={() => setShowFilter(false)}
          onApplyFilter={handleApplyFilter}
          onClear={handleClearFilter}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        showdt={showDt}
        handleCloseDt={() => setShowDt(false)}
        handledelete={handleDeleteGrade}
      />

      <ToastContainer />
    </>
  );
};

export default ManageGrade;